import React, { Fragment, useState, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Add, Edit, Delete, Save, Cancel, Pause, RotateLeft } from "@mui/icons-material"
import { Box, Typography, Button, Divider, IconButton, TextField, Select, MenuItem, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from "@mui/material"
import styled from "@emotion/styled"
import { convertMinutesToHours } from "../utils/timeUtils";
import { formatDate } from "../utils/dateUtils";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsProjectLocked } from "../utils/lockUtils";
import RunningTimer from './RunningTimer';
import { updateTime } from "../redux/ducks/time";
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { showAlert } from "../redux/slices/alerts";
import { stopTimer } from "../redux/ducks/time";
import { deleteTime } from "../redux/ducks/time";
import { restoreTime } from "../redux/ducks/time";
import { postTime } from "../redux/ducks/time";
import { useNavigate } from "react-router-dom";
import AddTimeRecordDialog from './AddTimeRecordDialog';
import AppDatePicker from './AppDatePicker';

const TabBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 2),
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        border: `0px`,
    }
}));

const TimeBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'isDeleted' })(({ theme, isDeleted }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
    flexWrap: 'nowrap',
    backgroundColor: isDeleted ? theme.palette.action.disabled : 'inherit',
    border: isDeleted ? `1px solid ${theme.palette.inverted.main}` : 'none',
    borderRadius: isDeleted ? theme.shape.borderRadius : 0,
}));

const TextContainer = styled(Box)({
    flexGrow: 1,
    minWidth: 0,
    marginRight: '8px',
});

const TitleRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
}));

const ParentChip = styled(Chip)({
    flexShrink: 0,
});

const DeletedTextContainer = styled(TextContainer)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    textDecoration: 'line-through',
}));

const TimeInfoBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: '100px',
    flexShrink: 0,
});

const TimeFirstLine = styled(Typography)(({ theme }) => ({
    textAlign: 'right',
    whiteSpace: 'nowrap',
}));

const TimeSecondLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    textAlign: 'right',
    whiteSpace: 'nowrap',
}));

const WideButton = styled(Button)(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
}));

const EditBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
}));

const EditFieldRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const EditStatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const EditActionsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
}));

const ActionsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
}));

const ItemLink = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    textDecoration: 'none',
    cursor: 'pointer',
}));

const UserLink = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    textDecoration: 'none',
    cursor: 'pointer',
}));

const ProjectTimeTab = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const canAddTime = hasPermission('addTime');
    const canEditTime = hasPermission('editTime');
    const canDeleteTime = hasPermission('deleteTime');
    const canRestoreTime = hasPermission('restoreTime');
    const [editingRecord, setEditingRecord] = useState(null);
    const [stopTimerDialogOpen, setStopTimerDialogOpen] = useState(false);
    const [currentStoppingTimer, setCurrentStoppingTimer] = useState(null);
    const [timerSummary, setTimerSummary] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [timeToDelete, setTimeToDelete] = useState(null);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [timeToRestore, setTimeToRestore] = useState(null);
    const [addTimeDialogOpen, setAddTimeDialogOpen] = useState(false);
    const project = useSelector(state => state.project.currentProject);
    const isLocked = useIsProjectLocked(project);

    const sortedTimeRecords = useMemo(() => {
        const projectRecords = (project?.timeRecords || []).map((record) => ({ record, taskName: null }));
        const taskRecords = (project?.tasks || []).flatMap((task) =>
            (task.timeRecords || []).map((record) => ({ record, taskName: task.name }))
        );
        return [...projectRecords, ...taskRecords].sort((a, b) => new Date(b.record.date) - new Date(a.record.date));
    }, [project?.timeRecords, project?.tasks]);

    const handleEditTime = (timeRecord) => {
        setEditingRecord({
            ...timeRecord,
            time: convertMinutesToHours(timeRecord).time,
            status: timeRecord.status || 'Pending',
            date: formatDate(timeRecord.date),
            billable: timeRecord.billable !== undefined ? timeRecord.billable : true,
        });
    };

    const handleStopTimer = (timeRecord) => {
        setCurrentStoppingTimer(timeRecord);
        setStopTimerDialogOpen(true);
    };

    const handleConfirmStopTimer = () => {
        if (currentStoppingTimer) {
            dispatch(stopTimer(currentStoppingTimer._id, { summary: timerSummary }, (response) => {
                if (response && response.timeRecord) {
                    setStopTimerDialogOpen(false);
                    setCurrentStoppingTimer(null);
                    setTimerSummary("");
                    dispatch(triggerRefresh({ triggerName: 'project' }));
                } else {
                    dispatch(showAlert({ severity: "error", message: response.message }));
                }
            }));
        }
    };

    const handleDeleteTime = (timeRecord) => {
        setTimeToDelete(timeRecord);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (timeToDelete) {
            dispatch(deleteTime(timeToDelete._id, (response) => {
                if (response && response.success) {
                    setDeleteDialogOpen(false);
                    setTimeToDelete(null);
                    dispatch(triggerRefresh({ triggerName: 'project' }));
                    dispatch(showAlert({ severity: "success", message: "Time record deleted successfully" }));
                } else {
                    dispatch(showAlert({ severity: "error", message: response.message || "Error deleting time record" }));
                }
            }));
        }
    };

    const handleRestoreTime = (timeRecord) => {
        setTimeToRestore(timeRecord);
        setRestoreDialogOpen(true);
    };

    const handleConfirmRestore = () => {
        if (timeToRestore) {
            dispatch(restoreTime(timeToRestore._id, (response) => {
                if (response && response.success) {
                    setRestoreDialogOpen(false);
                    setTimeToRestore(null);
                    dispatch(triggerRefresh({ triggerName: 'project' }));
                    dispatch(showAlert({ severity: "success", message: "Time record restored successfully" }));
                } else {
                    dispatch(showAlert({ severity: "error", message: response.message || "Error restoring time record" }));
                }
            }));
        }
    };

    const handleSaveEdit = () => {
        const updateData = {
            summary: editingRecord.summary,
            time: editingRecord.time * 60,
            date: editingRecord.date,
            status: editingRecord.status,
            billable: editingRecord.billable
        };
        dispatch(updateTime(editingRecord._id, updateData, (response) => {
            if (response && response.timeRecord) {
                setEditingRecord(null);
                dispatch(triggerRefresh({ triggerName: 'project' }));
            } else {
                dispatch(showAlert({ severity: "error", message: response.message }));
            }
        }));
    };

    const handleCancelEdit = () => {
        setEditingRecord(null);
    };

    const handleAddTimeClick = () => {
        setAddTimeDialogOpen(true);
    };

    const handleAddTime = (timeData) => {
        dispatch(postTime('project', project._id, timeData, (response) => {
            if (response && response.timeRecord) {
                dispatch(triggerRefresh({ triggerName: 'project' }));
                dispatch(showAlert({ severity: "success", message: "Time record added successfully" }));
            } else {
                dispatch(showAlert({ severity: "error", message: response?.message || "Error adding time record" }));
            }
        }));
    };

    const handleUserClick = (userId) => {
        navigate(`/user/${userId}`);
    };

    const handleItemClick = (itemId) => {
        navigate(`/account/${project.account.slug}/project/${project._id}`);
    };

    const renderEditForm = (record) => (
        <EditBox>
            <TextField
                label="Summary"
                value={record.summary}
                onChange={(e) => setEditingRecord({ ...record, summary: e.target.value })}
                fullWidth
            />
            <EditFieldRow>
                <TextField
                    label="Time (hours)"
                    type="number"
                    inputProps={{ min: 0, step: 0.25 }}
                    value={parseFloat(record.time)}
                    onChange={(e) => setEditingRecord({ ...record, time: e.target.value })}
                    fullWidth
                />
                <AppDatePicker
                    label="Date"
                    value={formatDate(record.date)}
                    onChange={(v) => setEditingRecord({ ...record, date: v })}
                />
            </EditFieldRow>
            <EditStatusRow>
                <Select
                    value={record.status}
                    onChange={(e) => setEditingRecord({ ...record, status: e.target.value })}
                    fullWidth
                >
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
                <FormControlLabel
                    control={
                        <Switch
                            checked={record.billable}
                            onChange={(e) => setEditingRecord({ ...record, billable: e.target.checked })}
                        />
                    }
                    label="Billable"
                />
            </EditStatusRow>
            <EditActionsRow>
                <Button variant="contained" color="primary" onClick={handleSaveEdit} startIcon={<Save />}>
                    Save
                </Button>
                <Button variant="outlined" onClick={handleCancelEdit} startIcon={<Cancel />}>
                    Cancel
                </Button>
            </EditActionsRow>
        </EditBox>
    );

    const renderTimeRecord = (timeRecord, taskName = null) => (
        <Fragment key={timeRecord._id}>
            {editingRecord && editingRecord._id === timeRecord._id ? (
                renderEditForm(editingRecord)
            ) : (
                <TimeBox isDeleted={timeRecord.isDeleted}>
                    {timeRecord.isDeleted ? (
                        <DeletedTextContainer>
                            <TitleRow>
                                <ParentChip size="small" variant="outlined" color={taskName ? 'info' : 'success'} label={taskName ? 'Task' : 'Project'} />
                                <ItemLink variant="subtitle2" onClick={() => handleItemClick(timeRecord._id)} noWrap>
                                    {taskName ? `${taskName} — ${timeRecord.summary}` : timeRecord.summary}
                                </ItemLink>
                            </TitleRow>
                            <UserLink variant="caption" noWrap onClick={() => handleUserClick(timeRecord.user._id)}>{timeRecord.user.fullName}</UserLink>
                        </DeletedTextContainer>
                    ) : (
                        <TextContainer>
                            <TitleRow>
                                <ParentChip size="small" variant="outlined" color={taskName ? 'info' : 'success'} label={taskName ? 'Task' : 'Project'} />
                                <ItemLink variant="subtitle2" onClick={() => handleItemClick(timeRecord._id)} noWrap>
                                    {taskName ? `${taskName} — ${timeRecord.summary}` : timeRecord.summary}
                                </ItemLink>
                            </TitleRow>
                            <UserLink variant="caption" noWrap onClick={() => handleUserClick(timeRecord.user._id)}>{timeRecord.user.fullName}</UserLink>
                        </TextContainer>
                    )}
                    <TimeInfoBox>
                        <TimeFirstLine variant="subtitle2">
                            {timeRecord.timerRunning ? (
                                <RunningTimer startTime={timeRecord.startedAt} />
                            ) : (
                                convertMinutesToHours(timeRecord).time
                            )}
                        </TimeFirstLine>
                        <TimeSecondLine variant="caption">{formatDate(timeRecord.date)}</TimeSecondLine>
                    </TimeInfoBox>
                    <ActionsRow>
                        {timeRecord.isDeleted ? (
                            <>
                                <Chip variant="outlined" size="small" color="error" label="Deleted" />
                                {canRestoreTime && !isLocked && (
                                    <IconButton size="small" onClick={() => handleRestoreTime(timeRecord)}>
                                        <RotateLeft fontSize="small" />
                                    </IconButton>
                                )}
                            </>
                        ) : (
                            <>
                                {timeRecord.timerRunning &&
                                    <IconButton size="small" onClick={() => handleStopTimer(timeRecord)}>
                                        <Pause fontSize="small" />
                                    </IconButton>
                                }
                                {!timeRecord.timerRunning && canEditTime && !isLocked && (
                                    <IconButton size="small" onClick={() => handleEditTime(timeRecord)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                )}
                                {canDeleteTime && !isLocked && (
                                    <IconButton size="small" onClick={() => handleDeleteTime(timeRecord)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                )}
                            </>
                        )}
                    </ActionsRow>
                </TimeBox>
            )}
        </Fragment>
    );

    return (
        <>
            <TabBox>
                {sortedTimeRecords.map(({ record, taskName }) => (
                    <Fragment key={record._id}>
                        {renderTimeRecord(record, taskName)}
                        <Divider />
                    </Fragment>
                ))}
            </TabBox>
            {canAddTime &&
                <WideButton variant="contained" onClick={handleAddTimeClick} disabled={isLocked}>
                    <Add /> Add Time Record
                </WideButton>
            }

            <Dialog open={stopTimerDialogOpen} onClose={() => setStopTimerDialogOpen(false)}>
                <DialogTitle>Stop Timer</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Summary"
                        type="text"
                        fullWidth
                        value={timerSummary}
                        onChange={(e) => setTimerSummary(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStopTimerDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmStopTimer} variant="contained">Stop Timer</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Time Record</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this time record?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
                <DialogTitle>Restore Time Record</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to restore this time record?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmRestore} variant="contained" color="primary">Restore</Button>
                </DialogActions>
            </Dialog>

            <AddTimeRecordDialog
                open={addTimeDialogOpen}
                onClose={() => setAddTimeDialogOpen(false)}
                onAddTime={handleAddTime}
                accountId={project?.account?._id || project?.account}
            />
        </>
    );
};

export default ProjectTimeTab;